import * as React from "react";
import {
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Container,
  FormControl,
  Toolbar,
  InputAdornment,
  CircularProgress,
  IconButton,
  Grid,
  Chip,
  OutlinedInput,
  FormControlLabel,
  Switch,
  Slider,
  Avatar,
  Card,
  CardContent,
  Collapse,
  LinearProgress,
  Fade,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  styled,
  alpha,
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import { LogoutRounded } from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import WorkIcon from "@mui/icons-material/Work";
import PetsIcon from "@mui/icons-material/Pets";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SchoolIcon from "@mui/icons-material/School";
import BusinessIcon from "@mui/icons-material/Business";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import BadgeIcon from "@mui/icons-material/Badge";
import DescriptionIcon from "@mui/icons-material/Description";
import AddIcon from "@mui/icons-material/Add";
import imageCompression from "browser-image-compression";

import OtsLogo from "../../../components/OtsLogo";
import title from "../../../components/title";
import { useTheme as useAppTheme } from "../../../context/ThemeContext";
import { RegisterTenant } from "../../../redux/reducer/TenantRegestration";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ValidateUser } from "../../../redux/reducer/ValidateUser";
import { useFormik } from "formik";
import LogoutHelper from "../../../Helper/LogoutHelper";
import {
  basicInformationSchema,
  personalInfoSchema,
  employmentSchema,
  lifestyleSchema,
  additionalInfoSchema,
} from "./validationSchema2";
import SignatureCapture from "../../../components/SignatureCapture";

// Styled Components
const AppBar = styled(MuiAppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: "1px solid rgba(18,32,58,0.08)",
  boxShadow: "0 14px 34px -22px rgba(18,32,58,0.55)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 18px 40px -22px rgba(18,32,58,0.6)",
  },
}));

const IconWrapper = styled(Box)(({ theme, active }) => ({
  width: 48,
  height: 48,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: active
    ? theme.palette.primary.main
    : alpha(theme.palette.primary.main, 0.08),
  color: active ? "#fff" : theme.palette.text.secondary,
  transition: "all 0.3s ease",
}));

const OptionCard = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  cursor: "pointer",
  border: selected
    ? `2px solid ${theme.palette.primary.main}`
    : "2px solid transparent",
  backgroundColor: selected
    ? alpha(theme.palette.primary.main, 0.06)
    : "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: alpha(theme.palette.primary.main, 0.45),
    transform: "translateY(-2px)",
  },
}));

const PhotoUploadBox = styled(Box)(({ theme, error }) => ({
  width: 120,
  height: 120,
  borderRadius: "50%",
  border: `3px dashed ${error ? theme.palette.error.main : "#ccc"}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  overflow: "hidden",
  transition: "all 0.3s ease",
  backgroundColor: error ? "rgba(244, 67, 54, 0.04)" : "transparent",
  "&:hover": {
    borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
    backgroundColor: error
      ? "rgba(244, 67, 54, 0.08)"
      : alpha(theme.palette.primary.main, 0.06),
  },
}));

const DocumentUploadBox = styled(Box)(({ theme, hasFile, error }) => ({
  border: `2px dashed ${
    error
      ? theme.palette.error.main
      : hasFile
      ? theme.palette.success.main
      : "#ccc"
  }`,
  borderRadius: 12,
  padding: theme.spacing(3),
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
  backgroundColor: hasFile
    ? "rgba(76, 175, 80, 0.04)"
    : error
    ? "rgba(244, 67, 54, 0.04)"
    : "#fafafa",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  },
}));

const DocumentPreview = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5),
  backgroundColor: "#f5f5f5",
  borderRadius: 8,
  marginTop: theme.spacing(1),
}));

const ModalOverlay = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  backdropFilter: "blur(4px)",
  cursor: "default",
}));

const ModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: 16,
  padding: theme.spacing(4),
  maxWidth: 500,
  width: "90%",
  textAlign: "center",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  animation: "slideIn 0.3s ease-out",
  cursor: "default",
  "@keyframes slideIn": {
    from: {
      opacity: 0,
      transform: "translateY(-20px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

const steps = [
  { label: "Basic Info", icon: <PersonIcon /> },
  { label: "Address & Family", icon: <HomeIcon /> },
  { label: "Employment", icon: <WorkIcon /> },
  { label: "Lifestyle", icon: <PetsIcon /> },
  { label: "Emergency Contact", icon: <ContactPhoneIcon /> },
];

const pakistaniLanguages = [
  "Urdu",
  "English",
  "Punjabi",
  "Saraiki",
  "Sindhi",
  "Pashto",
  "Balochi",
  "Brahui",
  "Hindko",
  "Kashmiri",
  "Pahari",
  "Pahari-Pothwari",
  "Gojri (Gujari)",
  "Shina",
  "Kohistani Shina",
  "Indus Kohistani",
  "Bateri",
  "Chilisso",
  "Gowro",
  "Torwali",
  "Gawri (Kalam Kohistani)",
  "Kalkoti",
  "Ushojo",
  "Khowar",
  "Kalasha (Kalasha-mun)",
  "Dameli",
  "Gawar-Bati",
  "Palula",
  "Yidgha",
  "Ormuri",
  "Burushaski",
  "Balti",
  "Wakhi",
  "Domaaki",
  "Kundal Shahi",
  "Jadgali",
  "Kachhi",
  "Dhatki",
  "Bagri",
  "Marwari",
  "Thari",
  "Parkari Koli",
  "Koli",
  "Kutchi (Kachchhi)",
  "Memoni",
  "Gujarati",
  "Dawoodi Bohra Gujarati",
  "Konkani",
  "Bengali",
  "Rohingya",
  "Burmese",
  "Tamil",
  "Telugu",
  "Malayalam",
  "Sinhala",
  "Persian (Farsi)",
  "Dari",
  "Hazaragi",
  "Arabic",
  "Turkmen",
  "Uzbek",
  "Kyrgyz",
  "Kazakh",
  "Uyghur",
  "Mandarin Chinese",
  "Cantonese",
  "Russian",
  "Armenian",
  "Nepali",
  "Dogri",
  "Chitrali (often used locally for Khowar)",
  "Siraiki Hindki (local variety)",
  "Bhaya",
  "Aer",
  "Loarki",
  "Sansi",
  "Od (Oadki)",
];

const incomeRanges = [
  { value: "below_25k", label: "Below 25,000" },
  { value: "25k_50k", label: "25,000 - 50,000" },
  { value: "50k_75k", label: "50,000 - 75,000" },
  { value: "75k_100k", label: "75,000 - 1,00,000" },
  { value: "100k_150k", label: "1,00,000 - 1,50,000" },
  { value: "above_150k", label: "Above 1,50,000" },
];

const moveInOptions = [
  { value: "immediate", label: "Immediately", desc: "Within a week" },
  { value: "15_days", label: "15 Days", desc: "Within 2 weeks" },
  { value: "1_month", label: "1 Month", desc: "Within a month" },
  { value: "2_months", label: "2 Months", desc: "Not in hurry" },
  { value: "flexible", label: "Flexible", desc: "No fixed timeline" },
];

const petTypes = ["Cat", "Dog", "Bird", "Fish", "Small Mammal", "Reptile", "Other"];

export default function TenantRegisteration() {
  title("Tenant Registration");
  const { theme: appTheme } = useAppTheme();
  const { loginUser } = useSelector((state) => state);
  const [activeStep, setActiveStep] = React.useState(0);
  const [buttonAction, setButtonAction] = React.useState(false);
  const [profileImage, setProfileImage] = React.useState(null);
  const [sameAddress, setSameAddress] = React.useState(false);
  const [languageSearch, setLanguageSearch] = React.useState("");
  const [languageOpen, setLanguageOpen] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);

  const fileInputRef = React.useRef(null);
  const cnicFrontRef = React.useRef(null);
  const cnicBackRef = React.useRef(null);
  const incomeProofRef = React.useRef(null);

  // New states for pets and vehicles
  const [currentPet, setCurrentPet] = React.useState({ type: "", count: 1 });
  const [currentVehicle, setCurrentVehicle] = React.useState({
    type: "",
    number: "",
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tenantRegistration = useSelector(
    (state) => state?.TenantRegestration?.loading
  );

  /* This page is full of `color="primary"` controls and it renders outside the
     tenant portal wrapper, so nothing was feeding it the brand colour and every
     one of them fell back to MUI's default blue. Bridging the picked theme into
     a local palette repaints the stepper, progress bar, inputs and buttons in
     one place — same trick the owner portal uses. */
  const muiTheme = React.useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: appTheme.primary,
            dark: appTheme.primaryHover,
            light: alpha(appTheme.primary, 0.35),
            contrastText: "#fff",
          },
          secondary: {
            main: appTheme.secondary,
            contrastText: "#10233b",
          },
          text: { primary: "#16203a", secondary: "#5b6880" },
          divider: "rgba(18,32,58,0.09)",
        },
        shape: { borderRadius: 12 },
        typography: {
          button: { textTransform: "none", fontWeight: 600 },
          h5: { fontWeight: 700 },
          h6: { fontWeight: 600 },
        },
        components: {
          MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
              root: { borderRadius: 10 },
              containedPrimary: {
                boxShadow: `0 10px 22px -14px ${appTheme.primary}`,
              },
            },
          },
          MuiChip: {
            styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } },
          },
          MuiLinearProgress: {
            styleOverrides: { root: { borderRadius: 999 } },
          },
        },
      }),
    [appTheme]
  );

  const initialValues = {
    // Step 1: Basic Info
    name: "",
    fatherName: "",
    cnicNo: "",
    mobileNumber: "",
    gender: "",
    maritalStatus: "",
    profilePhoto: "",
    profilePhotoFile: null,
    cnicFront: null,
    cnicFrontPreview: "",
    cnicBack: null,
    cnicBackPreview: "",

    // Step 2: Address & Family
    currentAddress: "",
    permanentAddress: "",
    tenantType: "",
    totalFamilyMembers: 1,
    language: [],
    cast: "",

    // Step 3: Employment
    employmentStatus: "",
    companyName: "",
    designation: "",
    instituteName: "",
    monthlyIncome: "",
    incomeProof: null,
    incomeProofPreview: "",
    incomeProofName: "",

    // Step 4: Lifestyle - UPDATED
    hasPets: false,
    pets: [], // Array of {type: string, count: number}
    hasVehicle: false,
    vehicles: [], // Array of {type: 'bike'|'car', number: string}
    preferredMoveIn: "",
    budgetRange: [10000, 50000],
    preferredAreas: [],

    // Step 5: Emergency Contact
    emergencyContactName: "",
    emergencyContactRelation: "",
    emergencyContactPhone: "",
    aboutMe: "",
    hasPreviousRental: false,
    previousLandlordName: "",
    previousLandlordPhone: "",

    // Digital Signature
    signature: "",
  };

  const getValidationSchema = React.useCallback(() => {
    switch (activeStep) {
      case 0:
        return basicInformationSchema;
      case 1:
        return personalInfoSchema;
      case 2:
        return employmentSchema;
      case 3:
        return lifestyleSchema;
      case 4:
        return additionalInfoSchema;
      default:
        return basicInformationSchema;
    }
  }, [activeStep]);

  const formik = useFormik({
    initialValues,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getValidationSchema(),
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (activeStep === steps.length - 1) {
        submitForm(values);
      } else {
        handleNext();
      }
    },
  });

  const formRef = React.useRef(null);
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    setFieldValue,
    submitCount,
  } = formik;

  React.useEffect(() => {
    if (submitCount > 0 && Object.keys(errors).length > 0) {
      const form = formRef.current;

      if (!form) return;

      const errorElement =
        form.querySelector(".Mui-error") ||
        form.querySelector('[aria-invalid="true"]');

      if (errorElement) {
        errorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        if (typeof errorElement.focus === "function") {
          errorElement.focus();
        }
      }
    }
  }, [errors, submitCount]);

  const submitForm = async (values) => {
    setButtonAction(true);

    const compressFile = async (file) => {
      if (!file) return null;
      const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
      };
      try {
        const compressedFile = await imageCompression(file, options);
        return compressedFile;
      } catch (err) {
        console.error("Image compression error:", err);
        return file;
      }
    };

    const files = {
      profilePhoto: await compressFile(values.profilePhotoFile),
      cnicFront: await compressFile(
        values.cnicFront instanceof File ? values.cnicFront : null
      ),
      cnicBack: await compressFile(
        values.cnicBack instanceof File ? values.cnicBack : null
      ),
      incomeProof: await compressFile(
        values.incomeProof instanceof File ? values.incomeProof : null
      ),
    };

    const finalValues = {
      ...values,
      mobileNumber: values.mobileNumber ? `03${values.mobileNumber}` : "",
      emergencyContactPhone: values.emergencyContactPhone
        ? `03${values.emergencyContactPhone}`
        : "",
      previousLandlordPhone: values.previousLandlordPhone
        ? `03${values.previousLandlordPhone}`
        : "",
      is_accept: false,
      is_reject: false,
      budgetMin: values.budgetRange[0],
      budgetMax: values.budgetRange[1],
      profilePhoto: "",
      cnicFront: "",
      cnicBack: "",
      incomeProof: "",
    };

    delete finalValues.profilePhotoFile;
    delete finalValues.cnicFrontPreview;
    delete finalValues.cnicBackPreview;
    delete finalValues.incomeProofPreview;
    delete finalValues.incomeProofName;
    delete finalValues.budgetRange;

    dispatch(RegisterTenant({ values: finalValues, files }))
      .then((res) => {
        if (
          res?.payload?.status === 200 ||
          res?.meta?.requestStatus === "fulfilled"
        ) {
          setShowSuccessModal(true);
          setButtonAction(false);
        } else {
          toast.error(res?.payload?.message || "Registration failed!", {
            position: "top-center",
          });
          setButtonAction(false);
        }
      })
      .catch(() => {
        toast.error("Server Error. Please try again.", {
          position: "top-center",
        });
        setButtonAction(false);
      });
  };

  const handleLoginAgain = () => {
    LogoutHelper.logout();
  };

  const stepSchemas = [
    basicInformationSchema,
    personalInfoSchema,
    employmentSchema,
    lifestyleSchema,
    additionalInfoSchema,
  ];

  const handleNext = async () => {
    try {
      await stepSchemas[activeStep].validate(values, { abortEarly: false });
      formik.setErrors({});
      formik.setTouched({});
      setActiveStep((prev) => prev + 1);
    } catch (err) {
      if (err.inner) {
        const touchedFields = {};
        const errorFields = {};
        err.inner.forEach((e) => {
          touchedFields[e.path] = true;
          errorFields[e.path] = e.message;
        });
        formik.setTouched((prev) => ({ ...prev, ...touchedFields }));
        formik.setErrors((prev) => ({ ...prev, ...errorFields }));
      }
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleCnicChange = (e) => {
    let value = e.target.value.replace(/\D/g, "").slice(0, 13);
    let formatted = value;
    if (value.length > 5) formatted = value.slice(0, 5) + "-" + value.slice(5);
    if (value.length > 12)
      formatted = formatted.slice(0, 13) + "-" + formatted.slice(13);
    setFieldValue("cnicNo", formatted);
  };

  const handlePhoneChange = (field) => (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 9);
    setFieldValue(field, value);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB", {
          position: "top-center",
        });
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setProfileImage(previewUrl);
      setFieldValue("profilePhoto", previewUrl);
      setFieldValue("profilePhotoFile", file);
    }
  };

  const handleSameAddress = (checked) => {
    setSameAddress(checked);
    if (checked) {
      setFieldValue("permanentAddress", values.currentAddress);
    }
  };

  const handleDocumentUpload =
    (field, previewField, nameField = null) =>
    (e) => {
      const file = e.target.files[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error("File size should be less than 5MB", {
            position: "top-center",
          });
          return;
        }
        const allowedTypes = [
          "image/jpeg",
          "image/png",
          "image/jpg",
          "application/pdf",
        ];
        if (!allowedTypes.includes(file.type)) {
          toast.error("Only JPG, PNG, or PDF files are allowed", {
            position: "top-center",
          });
          return;
        }

        const previewUrl = file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : "pdf";

        setFieldValue(field, file);
        setFieldValue(previewField, previewUrl);
        if (nameField) {
          setFieldValue(nameField, file.name);
        }
      }
    };

  const removeDocument =
    (field, previewField, nameField = null) =>
    () => {
      setFieldValue(field, null);
      setFieldValue(previewField, "");
      if (nameField) {
        setFieldValue(nameField, "");
      }
    };

  // Pet Management Functions
  const handleAddPet = () => {
    if (!currentPet.type) {
      return;
    }

    // Check if this pet type already exists
    const existingPet = values.pets.find((p) => p.type === currentPet.type);
    if (existingPet) {
      return;
    }

    setFieldValue("pets", [...values.pets, { ...currentPet }]);
    setCurrentPet({ type: "", count: 1 });
  };

  const handleRemovePet = (index) => {
    const updatedPets = values.pets.filter((_, i) => i !== index);
    setFieldValue("pets", updatedPets);
  };

  // Vehicle Management Functions
  const handleVehicleNumberChange = (e) => {
    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "");

    // Format: ABC-123 or ABC-1234
    if (value.length > 3 && !value.includes("-")) {
      value = value.slice(0, 3) + "-" + value.slice(3);
    }

    // Limit total length (ABC-1234 = 8 characters max)
    value = value.slice(0, 8);

    setCurrentVehicle({ ...currentVehicle, number: value });
  };

  const handleAddVehicle = () => {
    if (!currentVehicle.type) {
      return;
    }

    if (!currentVehicle.number || currentVehicle.number.length < 6) {
      return;
    }

    // Check if this vehicle number already exists
    const existingVehicle = values.vehicles.find(
      (v) => v.number === currentVehicle.number
    );
    if (existingVehicle) {
      return;
    }

    setFieldValue("vehicles", [...values.vehicles, { ...currentVehicle }]);
    setCurrentVehicle({ type: "", number: "" });
  };

  const handleRemoveVehicle = (index) => {
    const updatedVehicles = values.vehicles.filter((_, i) => i !== index);
    setFieldValue("vehicles", updatedVehicles);
  };

  const progress = ((activeStep + 1) / steps.length) * 100;

  // Step Content Renderers
  const renderStep1 = () => (
    <Fade in timeout={400}>
      <Box>
        <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
          Let's start with your basic information
        </Typography>

        {/* Profile Photo Upload */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <Box sx={{ textAlign: "center" }}>
            <PhotoUploadBox
              sx={{
                margin: "0 auto",
              }}
              onClick={() => fileInputRef.current?.click()}
              error={touched.profilePhoto && !values.profilePhoto}
            >
              {profileImage ? (
                <Avatar
                  src={profileImage}
                  sx={{ width: "100%", height: "100%" }}
                />
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    color:
                      touched.profilePhoto && !values.profilePhoto
                        ? "#d32f2f"
                        : "#999",
                  }}
                >
                  <CameraAltIcon sx={{ fontSize: 32 }} />
                  <Typography variant="caption" display="block">
                    Add Photo *
                  </Typography>
                </Box>
              )}
            </PhotoUploadBox>
            <input
              type="file"
              ref={fileInputRef}
              hidden
              accept="image/*"
              onChange={handleImageUpload}
            />
            <Typography
              variant="caption"
              color={
                touched.profilePhoto && !values.profilePhoto
                  ? "error"
                  : "text.secondary"
              }
              sx={{ mt: 1, display: "block" }}
            >
              Upload your profile photo *
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Full Name"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.name && (!values.name || Boolean(errors.name))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Father's Name"
              name="fatherName"
              value={values.fatherName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.fatherName &&
                (!values.fatherName || Boolean(errors.fatherName))
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="CNIC Number"
              name="cnicNo"
              placeholder="XXXXX-XXXXXXX-X"
              value={values.cnicNo}
              onChange={handleCnicChange}
              onBlur={handleBlur}
              error={
                touched.cnicNo && (!values.cnicNo || Boolean(errors.cnicNo))
              }
              helperText={formik.touched.cnicNo && formik.errors.cnicNo}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Mobile Number"
              name="mobileNumber"
              value={formik.values.mobileNumber}
              onChange={handlePhoneChange("mobileNumber")}
              onBlur={formik.handleBlur}
              error={
                formik.touched.mobileNumber &&
                (!values.mobileNumber || Boolean(formik.errors.mobileNumber))
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">03</InputAdornment>
                ),
              }}
              helperText={
                formik.touched.mobileNumber && formik.errors.mobileNumber
              }
            />
          </Grid>

          {/* Gender Selection */}
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color={
                touched.gender && !values.gender ? "error" : "text.secondary"
              }
              gutterBottom
            >
              Gender *
            </Typography>
            <ToggleButtonGroup
              value={values.gender}
              exclusive
              onChange={(e, val) => val && setFieldValue("gender", val)}
              fullWidth
              sx={{
                height: 48,
                border:
                  touched.gender && !values.gender
                    ? "1px solid #d32f2f"
                    : "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: 1,
                "& .MuiToggleButtonGroup-grouped": {
                  border: "none",
                  "&:not(:last-of-type)": {
                    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                  },
                },
              }}
            >
              {["male", "female", "other"].map((item) => (
                <ToggleButton
                  key={item}
                  value={item}
                  sx={{
                    flex: 1,
                    "&.Mui-selected": {
                      backgroundColor: "primary.main",
                      color: "#fff",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "primary.dark",
                    },
                  }}
                >
                  {item.charAt(0).toUpperCase() + item.slice(1)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>

          {/* Marital Status */}
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              color={
                touched.maritalStatus && !values.maritalStatus
                  ? "error"
                  : "text.secondary"
              }
              gutterBottom
            >
              Marital Status *
            </Typography>
            <ToggleButtonGroup
              value={values.maritalStatus}
              exclusive
              onChange={(e, val) => val && setFieldValue("maritalStatus", val)}
              fullWidth
              sx={{
                height: 48,
                border:
                  touched.maritalStatus && !values.maritalStatus
                    ? "1px solid #d32f2f"
                    : "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: 1,
                "& .MuiToggleButtonGroup-grouped": {
                  border: "none",
                  "&:not(:last-of-type)": {
                    borderRight: "1px solid rgba(0, 0, 0, 0.12)",
                  },
                },
              }}
            >
              <ToggleButton
                value="unmarried"
                sx={{
                  flex: 1,
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "#fff",
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: "primary.dark",
                  },
                }}
              >
                Single
              </ToggleButton>

              <ToggleButton
                value="married"
                sx={{
                  flex: 1,
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "#fff",
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: "primary.dark",
                  },
                }}
              >
                Married
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {/* CNIC Document Upload Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <BadgeIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                CNIC Documents
              </Typography>
              <Chip
                label="Required"
                size="small"
                color="error"
                variant="outlined"
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload clear photos of your CNIC (front and back). Accepted
              formats: JPG, PNG, PDF (Max 5MB each)
            </Typography>
          </Grid>

          {/* CNIC Front */}
          <Grid item xs={12} sm={6}>
            <input
              type="file"
              ref={cnicFrontRef}
              hidden
              accept="image/*"
              onChange={handleDocumentUpload("cnicFront", "cnicFrontPreview")}
            />
            <DocumentUploadBox
              hasFile={values.cnicFrontPreview}
              error={touched.cnicFront && !values.cnicFront}
              onClick={() =>
                !values.cnicFrontPreview && cnicFrontRef.current?.click()
              }
            >
              {values.cnicFrontPreview ? (
                <Box>
                  {values.cnicFrontPreview !== "pdf" ? (
                    <Box
                      component="img"
                      src={values.cnicFrontPreview}
                      sx={{
                        maxHeight: 120,
                        maxWidth: "100%",
                        borderRadius: 1,
                        mb: 1,
                      }}
                    />
                  ) : (
                    <InsertDriveFileIcon
                      sx={{ fontSize: 48, color: "success.main", mb: 1 }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    color="success.main"
                    fontWeight={600}
                  >
                    CNIC Front Uploaded
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeDocument("cnicFront", "cnicFrontPreview")();
                    }}
                    sx={{ mt: 1 }}
                  >
                    Remove
                  </Button>
                </Box>
              ) : (
                <Box>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 40,
                      color:
                        touched.cnicFront && !values.cnicFront
                          ? "error.main"
                          : "#999",
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={
                      touched.cnicFront && !values.cnicFront
                        ? "error.main"
                        : "inherit"
                    }
                  >
                    CNIC Front Side *
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click to upload
                  </Typography>
                </Box>
              )}
            </DocumentUploadBox>
          </Grid>

          {/* CNIC Back */}
          <Grid item xs={12} sm={6}>
            <input
              type="file"
              ref={cnicBackRef}
              hidden
              accept="image/*"
              onChange={handleDocumentUpload("cnicBack", "cnicBackPreview")}
            />
            <DocumentUploadBox
              hasFile={values.cnicBackPreview}
              error={touched.cnicBack && !values.cnicBack}
              onClick={() =>
                !values.cnicBackPreview && cnicBackRef.current?.click()
              }
            >
              {values.cnicBackPreview ? (
                <Box>
                  {values.cnicBackPreview !== "pdf" ? (
                    <Box
                      component="img"
                      src={values.cnicBackPreview}
                      sx={{
                        maxHeight: 120,
                        maxWidth: "100%",
                        borderRadius: 1,
                        mb: 1,
                      }}
                    />
                  ) : (
                    <InsertDriveFileIcon
                      sx={{ fontSize: 48, color: "success.main", mb: 1 }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    color="success.main"
                    fontWeight={600}
                  >
                    CNIC Back Uploaded
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeDocument("cnicBack", "cnicBackPreview")();
                    }}
                    sx={{ mt: 1 }}
                  >
                    Remove
                  </Button>
                </Box>
              ) : (
                <Box>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 40,
                      color:
                        touched.cnicBack && !values.cnicBack
                          ? "error.main"
                          : "#999",
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={
                      touched.cnicBack && !values.cnicBack
                        ? "error.main"
                        : "inherit"
                    }
                  >
                    CNIC Back Side *
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click to upload
                  </Typography>
                </Box>
              )}
            </DocumentUploadBox>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );

  const renderStep2 = () => (
    <Fade in timeout={400}>
      <Box>
        <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
          Where do you currently live?
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Current Address"
              name="currentAddress"
              multiline
              rows={2}
              value={values.currentAddress}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                formik.touched.currentAddress &&
                (!formik.values.currentAddress ||
                  Boolean(errors.currentAddress))
              }
              placeholder="Enter your current living address"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={sameAddress}
                  onChange={(e) => handleSameAddress(e.target.checked)}
                  color="primary"
                />
              }
              label="Permanent address is same as current address"
            />
          </Grid>

          <Grid item xs={12}>
            <Collapse in={!sameAddress}>
              <TextField
                fullWidth
                required
                label="Permanent Address"
                name="permanentAddress"
                multiline
                rows={2}
                value={values.permanentAddress}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.permanentAddress &&
                  (!values.permanentAddress || Boolean(errors.permanentAddress))
                }
                placeholder="Enter your permanent address"
              />
            </Collapse>
          </Grid>

          {/* Occupancy Type */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Who will be staying? *
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <OptionCard
                  selected={values.tenantType === "family"}
                  onClick={() => setFieldValue("tenantType", "family")}
                  elevation={values.tenantType === "family" ? 3 : 1}
                  sx={{
                    borderColor:
                      touched.tenantType && !values.tenantType
                        ? "error.main"
                        : undefined,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <IconWrapper active={values.tenantType === "family"}>
                      <FamilyRestroomIcon />
                    </IconWrapper>
                    <Box>
                      <Typography fontWeight={600}>Family</Typography>
                      <Typography variant="caption" color="text.secondary">
                        With spouse/children
                      </Typography>
                    </Box>
                  </Box>
                </OptionCard>
              </Grid>
              <Grid item xs={6}>
                <OptionCard
                  selected={values.tenantType === "bachelors"}
                  onClick={() => setFieldValue("tenantType", "bachelors")}
                  elevation={values.tenantType === "bachelors" ? 3 : 1}
                  sx={{
                    borderColor:
                      touched.tenantType && !values.tenantType
                        ? "error.main"
                        : undefined,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <IconWrapper active={values.tenantType === "bachelors"}>
                      <PersonOutlineIcon />
                    </IconWrapper>
                    <Box>
                      <Typography fontWeight={600}>Bachelors</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Single or with friends
                      </Typography>
                    </Box>
                  </Box>
                </OptionCard>
              </Grid>
            </Grid>
          </Grid>

          {/* Total Family Members */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Total Members (including you)
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() =>
                  setFieldValue(
                    "totalFamilyMembers",
                    Math.max(1, values.totalFamilyMembers - 1)
                  )
                }
                sx={{ minWidth: 40, height: 40 }}
              >
                -
              </Button>
              <Typography
                variant="h5"
                fontWeight={600}
                sx={{ minWidth: 40, textAlign: "center" }}
              >
                {values.totalFamilyMembers}
              </Typography>
              <Button
                variant="outlined"
                onClick={() =>
                  setFieldValue(
                    "totalFamilyMembers",
                    values.totalFamilyMembers + 1
                  )
                }
                sx={{ minWidth: 40, height: 40 }}
              >
                +
              </Button>
            </Box>
          </Grid>

          {/* Languages */}
          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              required
              error={
                touched.language &&
                (!values.language || values.language.length === 0)
              }
            >
              <InputLabel>Languages Spoken *</InputLabel>
              <Select
                multiple
                name="language"
                value={values.language || []}
                open={languageOpen}
                onOpen={() => setLanguageOpen(true)}
                onClose={() => setLanguageOpen(false)}
                onChange={(e) => {
                  setFieldValue("language", e.target.value.filter(Boolean));
                  setLanguageOpen(false);
                }}
                input={<OutlinedInput label="Languages Spoken *" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((lang) => (
                      <Chip
                        key={lang}
                        label={lang}
                        size="small"
                        onDelete={() =>
                          setFieldValue(
                            "language",
                            values.language.filter((l) => l !== lang)
                          )
                        }
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    ))}
                  </Box>
                )}
              >
                <Box sx={{ p: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search..."
                    value={languageSearch}
                    onChange={(e) => setLanguageSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </Box>
                {pakistaniLanguages
                  .filter((lang) =>
                    lang.toLowerCase().includes(languageSearch.toLowerCase())
                  )
                  .map((lang) => (
                    <MenuItem key={lang} value={lang}>
                      {lang}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Cast/Community */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Cast / Community"
              name="cast"
              value={values.cast}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.cast && (!values.cast || Boolean(errors.cast))}
            />
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );

  const renderStep3 = () => (
    <Fade in timeout={400}>
      <Box>
        <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
          What do you do for a living?
        </Typography>

        {/* Employment Status Cards */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Select Employment Status *
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            {
              value: "employed",
              label: "Employed",
              icon: <WorkIcon />,
              desc: "Working for a company",
            },
            {
              value: "self_employed",
              label: "Self Employed",
              icon: <BusinessIcon />,
              desc: "Own business",
            },
            {
              value: "student",
              label: "Student",
              icon: <SchoolIcon />,
              desc: "Currently studying",
            },
            {
              value: "retired",
              label: "Retired",
              icon: <PersonIcon />,
              desc: "Not working",
            },
          ].map((option) => (
            <Grid item xs={6} sm={3} key={option.value}>
              <OptionCard
                selected={values.employmentStatus === option.value}
                onClick={() => setFieldValue("employmentStatus", option.value)}
                elevation={values.employmentStatus === option.value ? 3 : 1}
                sx={{
                  height: "100%",
                  textAlign: "center",
                  py: 2,
                  borderColor:
                    touched.employmentStatus && !values.employmentStatus
                      ? "error.main"
                      : undefined,
                }}
              >
                <IconWrapper
                  active={values.employmentStatus === option.value}
                  sx={{ mx: "auto", mb: 1 }}
                >
                  {option.icon}
                </IconWrapper>
                <Typography fontWeight={600} variant="body2">
                  {option.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.desc}
                </Typography>
              </OptionCard>
            </Grid>
          ))}
        </Grid>

        {/* Conditional Fields */}
        <Collapse
          in={
            values.employmentStatus === "employed" ||
            values.employmentStatus === "self_employed"
          }
        >
          <Grid container spacing={3} sx={{ mb: 2, mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={
                  values.employmentStatus === "self_employed"
                    ? "Business Name"
                    : "Company Name"
                }
                name="companyName"
                value={values.companyName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.companyName &&
                  (!values.companyName || Boolean(errors.companyName))
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {values.employmentStatus === "employed" && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Designation"
                  name="designation"
                  value={values.designation}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.designation &&
                    (!values.designation || Boolean(errors.designation))
                  }
                />
              </Grid>
            )}
          </Grid>
        </Collapse>

        <Collapse in={values.employmentStatus === "student"}>
          <Grid container spacing={3} sx={{ mb: 2, mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Institution Name"
                name="instituteName"
                value={values.instituteName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.instituteName &&
                  (!values.instituteName || Boolean(errors.instituteName))
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SchoolIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Collapse>

        {/* Monthly Income */}
        <Typography
          variant="subtitle1"
          fontWeight={600}
          gutterBottom
          sx={{ mt: 2 }}
        >
          <AccountBalanceWalletIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          Monthly Income Range *
        </Typography>
        <Grid container spacing={1.5}>
          {incomeRanges.map((range) => (
            <Grid item xs={6} sm={4} key={range.value}>
              <OptionCard
                selected={values.monthlyIncome === range.value}
                onClick={() => setFieldValue("monthlyIncome", range.value)}
                elevation={values.monthlyIncome === range.value ? 2 : 0}
                sx={{
                  py: 1.5,
                  textAlign: "center",
                  borderColor:
                    touched.monthlyIncome && !values.monthlyIncome
                      ? "error.main"
                      : undefined,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={values.monthlyIncome === range.value ? 600 : 400}
                >
                  Rs. {range.label}
                </Typography>
              </OptionCard>
            </Grid>
          ))}
        </Grid>

        {/* Income Proof Document */}
        <Collapse
          in={
            values.employmentStatus === "employed" ||
            values.employmentStatus === "self_employed"
          }
        >
          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <DescriptionIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Income Proof Document
            </Typography>
            <Chip
              label="Optional"
              size="small"
              color="info"
              variant="outlined"
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload salary slip, bank statement, or any income proof document.
            This helps landlords verify your financial stability.
          </Typography>

          <input
            type="file"
            ref={incomeProofRef}
            hidden
            accept="image/*,.pdf"
            onChange={handleDocumentUpload(
              "incomeProof",
              "incomeProofPreview",
              "incomeProofName"
            )}
          />

          {values.incomeProofPreview ? (
            <DocumentPreview>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {values.incomeProofPreview !== "pdf" ? (
                  <Box
                    component="img"
                    src={values.incomeProofPreview}
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 1,
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <InsertDriveFileIcon
                    sx={{ fontSize: 40, color: "primary.main" }}
                  />
                )}
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {values.incomeProofName || "Income Proof"}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    Uploaded successfully
                  </Typography>
                </Box>
              </Box>
              <IconButton
                color="error"
                onClick={removeDocument(
                  "incomeProof",
                  "incomeProofPreview",
                  "incomeProofName"
                )}
              >
                <DeleteIcon />
              </IconButton>
            </DocumentPreview>
          ) : (
            <DocumentUploadBox onClick={() => incomeProofRef.current?.click()}>
              <CloudUploadIcon sx={{ fontSize: 40, color: "#999", mb: 1 }} />
              <Typography variant="body2" fontWeight={600}>
                Upload Income Proof
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Salary slip, bank statement, business proof, etc.
              </Typography>
            </DocumentUploadBox>
          )}
        </Collapse>
      </Box>
    </Fade>
  );

  const renderStep4 = () => (
    <Fade in timeout={400}>
      <Box>
        <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
          Tell us about your lifestyle
        </Typography>

        {/* Pets Section - UPDATED */}
        <StyledCard sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <IconWrapper active={values.hasPets}>
                  <PetsIcon />
                </IconWrapper>
                <Box>
                  <Typography fontWeight={600}>Do you have pets?</Typography>
                  <Typography variant="caption" color="text.secondary">
                    This helps find pet-friendly properties
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={values.hasPets}
                onChange={(e) => {
                  setFieldValue("hasPets", e.target.checked);
                  if (!e.target.checked) {
                    setFieldValue("pets", []);
                  }
                }}
                color="primary"
              />
            </Box>

            <Collapse in={values.hasPets}>
              <Divider sx={{ my: 2 }} />

              {/* Add Pet Form */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={7}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Pet Type</InputLabel>
                    <Select
                      value={currentPet.type}
                      label="Pet Type"
                      onChange={(e) =>
                        setCurrentPet({ ...currentPet, type: e.target.value })
                      }
                    >
                      {petTypes
                        .filter(
                          (petType) =>
                            !values.pets.some((p) => p.type === petType)
                        )
                        .map((petType) => (
                          <MenuItem key={petType} value={petType}>
                            {petType}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Count"
                    type="number"
                    value={currentPet.count}
                    onChange={(e) =>
                      setCurrentPet({
                        ...currentPet,
                        count: Math.max(1, parseInt(e.target.value) || 1),
                      })
                    }
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    onClick={handleAddPet}
                    disabled={!currentPet.type}
                    sx={{ height: "40px" }}
                    title="Click to add pet"
                  >
                    <AddIcon />
                  </Button>
                </Grid>
              </Grid>

              {/* Helper text */}
              {currentPet.type && (
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ display: "block", mb: 1, ml: 1 }}
                >
                  ⬆️ Click the + button to add this pet
                </Typography>
              )}

              {/* Error message if no pets added */}
              {touched.pets &&
                errors.pets &&
                typeof errors.pets === "string" && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.pets}
                  </Alert>
                )}

              {/* Pet List */}
              {values.pets.length > 0 && (
                <List dense>
                  {values.pets.map((pet, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        bgcolor: "#f5f5f5",
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      <ListItemText
                        primary={`${pet.type}`}
                        secondary={`Count: ${pet.count}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleRemovePet(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Collapse>
          </CardContent>
        </StyledCard>

        {/* Vehicle Section - UPDATED */}
        <StyledCard sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <IconWrapper active={values.hasVehicle}>
                  <DirectionsCarIcon />
                </IconWrapper>
                <Box>
                  <Typography fontWeight={600}>
                    Do you own a vehicle?
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    To check parking availability
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={values.hasVehicle}
                onChange={(e) => {
                  setFieldValue("hasVehicle", e.target.checked);
                  if (!e.target.checked) {
                    setFieldValue("vehicles", []);
                  }
                }}
                color="primary"
              />
            </Box>

            <Collapse in={values.hasVehicle}>
              <Divider sx={{ my: 2 }} />

              {/* Add Vehicle Form */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={5}>
                  <ToggleButtonGroup
                    value={currentVehicle.type}
                    exclusive
                    onChange={(e, val) =>
                      val && setCurrentVehicle({ ...currentVehicle, type: val })
                    }
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiToggleButton-root.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                      },
                    }}
                  >
                    <ToggleButton value="bike">
                      <TwoWheelerIcon sx={{ mr: 0.5, fontSize: 18 }} /> Bike
                    </ToggleButton>
                    <ToggleButton value="car">
                      <DirectionsCarIcon sx={{ mr: 0.5, fontSize: 18 }} /> Car
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Vehicle Number"
                    placeholder="ABC-123"
                    value={currentVehicle.number}
                    onChange={handleVehicleNumberChange}
                    helperText="Format: ABC-123"
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    onClick={handleAddVehicle}
                    disabled={
                      !currentVehicle.type || currentVehicle.number.length < 6
                    }
                    sx={{ height: "40px" }}
                    title="Click to add vehicle"
                  >
                    <AddIcon />
                  </Button>
                </Grid>
              </Grid>

              {/* Helper text */}
              {currentVehicle.type && currentVehicle.number && (
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ display: "block", mb: 1, ml: 1 }}
                >
                  ⬆️ Click the + button to add this vehicle
                </Typography>
              )}

              {/* Error message if no vehicles added */}
              {touched.vehicles &&
                errors.vehicles &&
                typeof errors.vehicles === "string" && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.vehicles}
                  </Alert>
                )}

              {/* Vehicle List */}
              {values.vehicles.length > 0 && (
                <List dense>
                  {values.vehicles.map((vehicle, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        bgcolor: "#f5f5f5",
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {vehicle.type === "bike" ? (
                              <TwoWheelerIcon fontSize="small" />
                            ) : (
                              <DirectionsCarIcon fontSize="small" />
                            )}
                            <Typography variant="body2" fontWeight={600}>
                              {vehicle.type.toUpperCase()}
                            </Typography>
                          </Box>
                        }
                        secondary={`Number: ${vehicle.number}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleRemoveVehicle(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Collapse>
          </CardContent>
        </StyledCard>

        {/* Move-in Timeline */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          <CalendarMonthIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          When do you want to move in? *
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {moveInOptions.map((option) => (
            <Grid item xs={4} sm={2.4} key={option.value}>
              <OptionCard
                selected={values.preferredMoveIn === option.value}
                onClick={() => setFieldValue("preferredMoveIn", option.value)}
                elevation={values.preferredMoveIn === option.value ? 2 : 0}
                sx={{
                  textAlign: "center",
                  py: 1.5,
                  borderColor:
                    touched.preferredMoveIn && !values.preferredMoveIn
                      ? "error.main"
                      : undefined,
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {option.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.desc}
                </Typography>
              </OptionCard>
            </Grid>
          ))}
        </Grid>

        {/* Budget Range */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          <AccountBalanceWalletIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          Your Budget Range (Monthly Rent)
        </Typography>
        <Box sx={{ px: 2 }}>
          <Slider
            value={values.budgetRange}
            onChange={(e, val) => setFieldValue("budgetRange", val)}
            valueLabelDisplay="on"
            valueLabelFormat={(val) => `Rs. ${val.toLocaleString()}`}
            min={5000}
            max={200000}
            step={5000}
            sx={{ mt: 4 }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="caption" color="text.secondary">
              Rs. 5,000
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Rs. 2,00,000
            </Typography>
          </Box>
        </Box>
      </Box>
    </Fade>
  );

  const renderStep5 = () => (
    <Fade in timeout={400}>
      <Box>
        <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 1 }}>
          Almost done! Emergency contact details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          This information will only be used in case of emergencies
        </Typography>

        <StyledCard sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              <ContactPhoneIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              Emergency Contact *
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Contact Person Name"
                  name="emergencyContactName"
                  value={values.emergencyContactName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.emergencyContactName &&
                    (!values.emergencyContactName ||
                      Boolean(errors.emergencyContactName))
                  }
                  helperText={
                    touched.emergencyContactName && errors.emergencyContactName
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  required
                  error={
                    touched.emergencyContactRelation &&
                    !values.emergencyContactRelation
                  }
                >
                  <InputLabel>Relationship *</InputLabel>
                  <Select
                    value={values.emergencyContactRelation}
                    label="Relationship *"
                    onChange={(e) =>
                      setFieldValue("emergencyContactRelation", e.target.value)
                    }
                  >
                    <MenuItem value="father">Father</MenuItem>
                    <MenuItem value="mother">Mother</MenuItem>
                    <MenuItem value="spouse">Spouse</MenuItem>
                    <MenuItem value="sibling">Sibling</MenuItem>
                    <MenuItem value="friend">Friend</MenuItem>
                    <MenuItem value="relative">Other Relative</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Contact Number"
                  name="emergencyContactPhone"
                  value={values.emergencyContactPhone}
                  onChange={handlePhoneChange("emergencyContactPhone")}
                  onBlur={handleBlur}
                  error={
                    touched.emergencyContactPhone &&
                    (!values.emergencyContactPhone ||
                      Boolean(errors.emergencyContactPhone))
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">03</InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </StyledCard>

        {/* About Me */}
        <TextField
          fullWidth
          label="About Me (Optional)"
          name="aboutMe"
          multiline
          rows={3}
          value={values.aboutMe}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.aboutMe && Boolean(errors.aboutMe)}
          helperText={`${values.aboutMe.length}/300 characters`}
          placeholder="Write a brief introduction about yourself that landlords will see..."
          sx={{ mb: 3 }}
        />

        {/* Previous Rental */}
        <StyledCard>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography fontWeight={600}>
                  Previous Rental Experience
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Add reference from previous landlord (optional)
                </Typography>
              </Box>
              <Switch
                checked={values.hasPreviousRental}
                onChange={(e) =>
                  setFieldValue("hasPreviousRental", e.target.checked)
                }
                color="primary"
              />
            </Box>

            <Collapse in={values.hasPreviousRental}>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Previous Landlord Name"
                    name="previousLandlordName"
                    value={values.previousLandlordName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Landlord Phone"
                    name="previousLandlordPhone"
                    value={values.previousLandlordPhone}
                    onChange={handlePhoneChange("previousLandlordPhone")}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">03</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </CardContent>
        </StyledCard>

        {/* Digital Signature */}
        <StyledCard sx={{ mt: 3 }}>
          <CardContent>
            <SignatureCapture
              label="Digital Signature"
              existingSignature={values.signature}
              onSignatureChange={(sig) => setFieldValue("signature", sig)}
              error={touched.signature && !values.signature}
              helperText={
                touched.signature && !values.signature
                  ? "Signature is required"
                  : ""
              }
              primaryColor={appTheme.primary}
            />
          </CardContent>
        </StyledCard>

        {/* Completion Message */}
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>You're all set!</strong> Click "Complete Registration" to
            finish your profile. You can always update this information later
            from your profile settings.
          </Typography>
        </Alert>
      </Box>
    </Fade>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderStep1();
      case 1:
        return renderStep2();
      case 2:
        return renderStep3();
      case 3:
        return renderStep4();
      case 4:
        return renderStep5();
      default:
        return null;
    }
  };

  return (
    <MuiThemeProvider theme={muiTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          background: `linear-gradient(180deg, ${alpha(
            appTheme.primary,
            0.07
          )} 0%, #f6f8fb 320px)`,
        }}
      >
        {/* Brand header — the mark is drawn from the theme instead of the flat
            artwork, so it stays crisp at toolbar height and the wordmark reads
            as text rather than a stretched image. */}
        <AppBar position="fixed" elevation={0}>
          <Toolbar
            sx={{
              minHeight: { xs: 64, md: 74 },
              px: { xs: 1.5, md: 3 },
              gap: 1.5,
              justifyContent: "space-between",
              background: `linear-gradient(115deg, ${appTheme.navBg} 0%, ${appTheme.navBg} 45%, ${appTheme.primary}40 100%)`,
              borderBottom: `1px solid ${appTheme.secondary}33`,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.4, minWidth: 0 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 44,
                  width: 44,
                  flexShrink: 0,
                  borderRadius: "14px",
                  bgcolor: "rgba(255,255,255,0.1)",
                  border: `1px solid ${appTheme.secondary}40`,
                }}
              >
                <OtsLogo height={30} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  noWrap
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: { xs: 15, md: 17 },
                    lineHeight: 1.2,
                    letterSpacing: 0.3,
                  }}
                >
                  Owner Tenant Solution
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    color: appTheme.secondary,
                    fontSize: { xs: 9.5, md: 10.5 },
                    letterSpacing: 1.6,
                    textTransform: "uppercase",
                  }}
                >
                  Tenant Registration
                </Typography>
              </Box>
            </Box>

            <Button
              onClick={() => LogoutHelper.logout()}
              startIcon={<LogoutRounded />}
              sx={{
                flexShrink: 0,
                color: "rgba(255,255,255,0.85)",
                borderRadius: "999px",
                px: { xs: 1.4, md: 2 },
                border: "1px solid rgba(255,255,255,0.18)",
                "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 } },
                "&:hover": {
                  color: "#fff",
                  bgcolor: "rgba(244,67,54,0.22)",
                  borderColor: "rgba(244,67,54,0.45)",
                },
              }}
            >
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                Logout
              </Box>
            </Button>
          </Toolbar>
        </AppBar>

        <Container
          maxWidth="md"
          sx={{ pt: { xs: 11, md: 13 }, pb: 6 }}
        >
        {tenantRegistration && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "rgba(255,255,255,0.8)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={60} />
          </Box>
        )}

        {/* Page title */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography
            variant="h4"
            fontWeight="bold"
            gutterBottom
            sx={{
              background: `linear-gradient(120deg, ${appTheme.primary} 0%, ${appTheme.secondary} 100%)`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: 0.5,
              fontSize: { xs: "1.6rem", md: "2.125rem" },
            }}
          >
            TENANT REGISTRATION
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Complete your profile so owners can review and approve your
            application
          </Typography>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Step {activeStep + 1} of {steps.length}
            </Typography>
            <Typography variant="body2" color="primary" fontWeight={600}>
              {Math.round(progress) - 20}% Complete
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(appTheme.primary, 0.14),
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
                background: `linear-gradient(90deg, ${appTheme.primary} 0%, ${appTheme.secondary} 100%)`,
              },
            }}
          />
        </Box>

        {/* Step Icons */}
        <Box
          sx={{
            display: "flex",
            "@media (max-width: 360px)": {
              display: "none",
            },
            justifyContent: "center",
            gap: 1,
            mb: 4,
          }}
        >
          {steps.map((step, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                opacity: index <= activeStep ? 1 : 0.4,
                margin: {
                  xs: "5px",
                  sm: "8px",
                  md: "10px",
                },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    index < activeStep
                      ? "success.main"
                      : index === activeStep
                      ? "primary.main"
                      : "#e6e9ef",
                  color: index <= activeStep ? "#fff" : "#99a1b0",
                  boxShadow:
                    index === activeStep
                      ? `0 0 0 4px ${alpha(appTheme.primary, 0.16)}`
                      : "none",
                  transition: "all 0.3s ease",
                }}
              >
                {index < activeStep ? <CheckCircleIcon /> : step.icon}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  fontWeight: index === activeStep ? 600 : 400,
                  color:
                    index === activeStep ? "primary.main" : "text.secondary",
                }}
              >
                {step.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Form Content */}
        <StyledCard sx={{ p: { xs: 2, sm: 4 } }}>
          <form onSubmit={formik.handleSubmit} noValidate ref={formRef}>
            {getStepContent(activeStep)}

            {/* Navigation Buttons */}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
            >
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={activeStep === 0}
                sx={{ px: 4 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={buttonAction}
                sx={{ px: 4 }}
              >
                {activeStep === steps.length - 1
                  ? buttonAction
                    ? "Submitting..."
                    : "Complete Registration"
                  : "Continue"}
              </Button>
            </Box>
          </form>
        </StyledCard>

        {/* Success Modal */}
        {showSuccessModal && (
          <ModalOverlay onClick={(e) => e.stopPropagation()}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <Box sx={{ mb: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 80, color: "success.main" }} />
              </Box>
              <Typography
                variant="h4"
                fontWeight={700}
                gutterBottom
                color="success.main"
              >
                Registration Successful! 🎉
              </Typography>
              <Typography
                variant="body1"
                color="text.primary"
                sx={{ mb: 1, fontWeight: 500 }}
              >
                Your profile has been submitted successfully!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Our system will approve your profile in a few seconds. Please
                wait while we verify your information.
              </Typography>

              <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
                <Typography variant="body2">
                  <strong>Important:</strong> Please login again to access your
                  tenant dashboard with updated profile data.
                </Typography>
              </Alert>

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleLoginAgain}
                sx={{
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: 3,
                  "&:hover": {
                    boxShadow: 6,
                  },
                }}
              >
                Login Again
              </Button>
            </ModalContent>
          </ModalOverlay>
        )}
        </Container>

        <ToastContainer />
      </Box>
    </MuiThemeProvider>
  );
}
